import React, { Component } from 'react';
import PropTypes from 'prop-types';
import sha1 from 'sha1';
import cx from 'classnames';
import Tooltip from 'react-scale/components/website/index/how-it-works/Tooltip';

export default class TaskResponsePolygonAnnotation extends Component {
  static propTypes = {
    annotations: PropTypes.array.isRequired,
    bgTransparent: PropTypes.bool,
    hideTooltip: PropTypes.bool,
    hover: PropTypes.bool,
    imageURL: PropTypes.string.isRequired,
    responsive: PropTypes.bool,
    imageZoomFactor: PropTypes.number,
    rounded: PropTypes.bool
  };

  static defaultProps = {
    bgTransparent: false,
    hideTooltip: false,
    hover: false,
    responsive: false,
    imageZoomFactor: 1,
    rounded: false
  };

  constructor(props) {
    super(props);

    this.setActiveAnnotation = this.setActiveAnnotation.bind(this);
    this.hideAnnotation = this.hideAnnotation.bind(this);
    this.showAnnotations = this.showAnnotations.bind(this);
  }

  state = {
    tooltip: { pageX: 0, pageY: 0, show: false, content: '' },
    showAnnotations: false,
    alwaysShowLabels: false
  };

  setActiveAnnotation(annotation) {
    if (this.props.hideTooltip) return;

    return e => {
      const { top: pageY, left: pageX } = e.target.getBoundingClientRect();
      const { top: offsetY, left: offsetX } = this.content.getBoundingClientRect();

      const content = this.getTooltip(annotation);

      this.setState({
        tooltip: {
          pageX: pageX - offsetX,
          pageY: pageY - offsetY,
          show: true,
          content
        }
      });
    };
  }

  hideAnnotation() {
    this.setState(state => ({
      tooltip: { ...state.tooltip, show: false }
    }));
  }

  annotationToPercent(annotation) {
    const vertices = annotation.vertices.map(vertex => ({
      xPercent: (100.0 * vertex.x) / (this.imageRef.naturalWidth / this.props.imageZoomFactor),
      yPercent: (100.0 * vertex.y) / (this.imageRef.naturalHeight / this.props.imageZoomFactor)
    }));
    return {
      label: annotation.label,
      vertices
    };
  }

  showAnnotations() {
    this.setState({ showAnnotations: true });
  }

  keyForAnnotation(annotation) {
    return sha1(JSON.stringify(annotation));
  }

  getTooltip({ label, vertices }) {
    return (
      <span>
        {label} annotation - {vertices.length} vertices
      </span>
    );
  }

  renderPolygons() {
    return (
      <svg
        className={cx('polygons-svg', { hover: this.props.hover })}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        {this.props.annotations.map(annotation => {
          const percentAnnotation = this.annotationToPercent(annotation);
          const points = percentAnnotation.vertices
            .map(vertex => `${vertex.xPercent},${vertex.yPercent}`)
            .join(' ');
          return (
            <polygon
              points={points}
              key={this.keyForAnnotation(annotation)}
              onMouseEnter={this.setActiveAnnotation(annotation)}
              onMouseLeave={this.hideAnnotation}
              fill="rgba(0,256,0,0.35)"
              stroke="green"
              strokeWidth="0.3"
            />
          );
        })}

        <style jsx>{`
          .polygons-svg {
            opacity: 0;
            animation-name: fade;
            animation-duration: 0.5s;
            animation-fill-mode: both;
            animation-delay: 0.5s;
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
          }
          
          .polygons-svg.hover polygon {
            transition: fill 300ms;
            cursor: default;
          }
          .polygons-svg.hover polygon:hover {
            fill: rgba(0, 255, 0, 0.7);
          }
          
          @keyframes fade {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
        `}</style>
      </svg>
    );
  }

  render() {
    const { tooltip } = this.state;

    return (
      <div
        style={{ position: 'relative' }}
        ref={ref => {
          this.content = ref;
        }}
      >
        <div className="task-response__wrapper">
          <div
            ref={ref => {
              this.response = ref;
            }}
            className={cx('task-response__annotation', {
              'task-response__annotation--bg-transparent': this.props.bgTransparent,
              'task-response__annotation--max-height': !this.props.responsive,
              'task-response__annotation--rounded': this.props.rounded
            })}
          >
            <div
              style={{
                position: 'relative',
                margin: '0 auto',
                ...(this.props.responsive ? {} : { maxHeight: 600 })
              }}
            >
              <img
                key={this.props.imageURL}
                onLoad={this.showAnnotations}
                ref={ref => {
                  this.imageRef = ref;
                }}
                className={cx('task-response__annotation-image', {
                  'task-response__annotation-image--max-height': !this.props.responsive,
                  'task-response__annotation-image--responsive': this.props.responsive,
                  'task-response__annotation-image--transparent': this.props.transparent
                })}
                src={this.props.imageURL}
                style={this.imageStyle}
              />
              {this.state.showAnnotations && this.renderPolygons()}
            </div>
          </div>
          {this.state.showAnnotations && <Tooltip {...tooltip} />}
        </div>
        <style jsx>{`
          .task-response__annotation {
            position: relative;
            width: 100%;
            background-color: #dcdcdc;
          }
          
          .task-response__annotation--rounded {
            overflow: hidden;
            border-radius: var(--border-radius);
          }
          
          .task-response__annotation--max-height {
            max-height: 600px;
          }
          
          .task-response__annotation--bg-transparent {
            background-color: transparent;
          }
          
          .task-response__annotation-image--transparent {
            opacity: 0;
          }
          
          .task-response__annotation-image--max-height {
            max-height: 600px;
          }
          
          .task-response__annotation-image--responsive {
            width: 100%;
          }
          
          .task-response__status-bar {
            width: 100%;
            background-color: #26b2c3;
            height: 20px;
            color: #fff;
            font-size: 12px;
            align-items: center;
            padding-left: 14px;
            padding-right: 14px;
            display: flex;
            box-sizing: border-box;
          }
        `}</style>
      </div>
    );
  }
}
